import React, { useState } from 'react'
import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Message from './../components/Message'
import Loader from './../components/Loader'
import { useGetInsurancesQuery, useDeleteInsuranceMutation } from './../slices/insurancesApiSlice'
import { toast } from 'react-toastify'

const StaffInsurance = () => {
  // UI state
  const [filter, setFilter] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // Data fetching
  const { data, isLoading, error, refetch } = useGetInsurancesQuery(pageNumber)
  const [deleteInsurance, { isLoading: loadingDelete }] = useDeleteInsuranceMutation()

  // Delete handler
  const deleteHandler = async (id) => {
    if (window.confirm('Delete this insurance?')) {
      try {
        await deleteInsurance(id).unwrap()
        toast.success('Insurance deleted')
        refetch()
      } catch (err) {
        toast.error(err?.data?.message || 'Delete failed')
      }
    }
  }

  // 1) Date‐range filter, 2) general filter (note/user/customer/product), 
  // 3) product‐only filter
  const filterInsurances = () => {
    if (!data?.insurances) return []

    // 1) date range
    let result = data.insurances.filter((insurance) => {
      const d = new Date(insurance.date)
      return (
        (!startDate || d >= new Date(startDate)) &&
        (!endDate   || d <= new Date(endDate))
      )
    })

    // 2) general text filter
    if (filter.trim()) {
      const lf = filter.toLowerCase()
      result = result.filter((insurance) => {
        return (
          insurance.note?.toLowerCase().includes(lf) ||
          insurance.customerDetails?.company?.toLowerCase().includes(lf) ||
          insurance.customerDetails?.name?.toLowerCase().includes(lf) ||
          insurance.customerDetails?.address?.toLowerCase().includes(lf) ||
          insurance.username?.toLowerCase().includes(lf) ||
          insurance.insuranceItems.some((it) => it.name.toLowerCase().includes(lf))
        )
      })
    }

    // 3) product‐only filter
    if (productSearch.trim()) {
      const lp = productSearch.toLowerCase()
      result = result
        .map((insurance) => ({
          ...insurance,
          insuranceItems: insurance.insuranceItems.filter((it) =>
            it.name.toLowerCase().includes(lp)
          ),
        }))
        .filter((insurance) => insurance.insuranceItems.length > 0)
    }

    return result
  }

  // Total calculation
  const totalInsurances = filterInsurances().reduce((sum, insurance) => {
    return (
      sum +
      insurance.insuranceItems.reduce(
        (sub, it) => sub + it.qty * it.price,
        0
      )
    )
  }, 0)

  // Pagination handler
  const goPage = (p) => setPageNumber(p)

  return (
    <>
      <Row className="align-items-center mb-3">
        <Col><h1>Insurance Bills</h1></Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter note/user/company/customer or any product"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total Bill: D{totalInsurances.toFixed(2)}</h4>
          <h4>GTotal : ${(totalInsurances * 1.4).toFixed(2)}</h4>
        </Col>
      </Row>

      {/* New product‐only filter */}
      <Row className="mb-4">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name only"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error fetching insurances'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>GTOTAL</th>
                <th>COMPANY</th>
                <th>PATIENT</th>
                <th>SERIAL</th>
                <th>SUBCOM</th>
                <th>TEL</th>
                <th>NOTE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterInsurances().map((insurance) =>
                insurance.insuranceItems.map((item) => (
                  <tr key={`${insurance._id}-${item.product}`}>
                    <td>
                      {new Date(insurance.date).toLocaleDateString()}
                    </td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>
                      D{(item.qty * item.price).toFixed(0)}
                    </td>
                    <td>{(item.qty * item.price * 1.4).toFixed(0)}</td>
                    <td>{insurance.customerDetails.company || 'N/A'}</td>
                    <td>
                      {insurance.customerDetails.name || 'N/A'}
                    </td>
                    <td>
                      {insurance.customerDetails.serialNumber || 'N/A'}
                    </td>
                    <td>
                      {insurance.customerDetails.address || 'N/A'}
                    </td>
                    <td>
                      {insurance.customerDetails.telephone || 'N/A'}
                    </td>
                    <td>{insurance.note}</td>
                    <td  style={{display: "None"}}>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt=""
                          style={{
                            width: 10,
                            height: 10,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/insurance/${insurance._id}`}
                        variant="light"
                        size="sm"
                      >
                        Details
                      </Button>
                      <Button
                        style={{display: "None"}}
                        variant="danger"
                        size="sm"
                        className="ms-2"
                        onClick={() => deleteHandler(insurance._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => goPage(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  )
}

export default StaffInsurance